import { useEffect, useMemo, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { useNavigate } from 'react-router-dom';
import { getIntegrationsPaginated } from '../../../../redux/slices/lists/integrationsSlice';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import TableViewLayout from 'layouts/TableViewLayout';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

const toActionEntity = (row: any): ActionEntity => ({ type: 'integration', id: row.integration_id, name: row.name });

const ListIntegrations = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { integrations, config: headerConfig, filtersConfig, pagination, status, error, loading } = useAppSelector((state) => state.listOfIntegrations);

  const userData = useAppSelector((state) => state.auth.userData);

  const pageName = 'integrations';
  const [showSidebar, setShowSidebar] = useState(false);

  const isLoading = loading.getIntegrationsPaginated;

  useEffect(() => {
    if (status === null) {
      dispatch(getIntegrationsPaginated({}) as any);
    }
  }, [dispatch, status]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: getIntegrationsPaginated,
    columnFormats: { created_at: 'datetime', updated_at: 'datetime' },
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh, navigate });
      return <ActionDropdown actions={actions} items={['edit', 'integrationNodes', 'addToTransportIntegration', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });
  const columns = [...baseColumns, actionsColumn];

  const formattedData = useMemo(() => {
    return getFormattedData(integrations || []);
  }, [integrations]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row) => String(row.integration_id),
  });

  const { currentPage, totalPages, totalItems } = pagination;

  const selectionEntities = table.getSelectedRowModel().rows.map((r: any) => toActionEntity(r.original));
  const toolbarActions = getActions({ entities: selectionEntities, refresh, navigate });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={(headerConfig ?? []).map((c: any) => ({
        key: c.header_key,
        label: c.label,
      }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refresh}
      isLoading={isLoading}
      isSilentRefresh={isSilentRefresh}
      toolbarButtons={
        <>
          <ActionButton actions={toolbarActions} action="createIntegration" variant="primary" />
          <ActionButton actions={toolbarActions} action="addToTransportIntegration" className="ms-2" variant="secondary" label="Add selected to transport" />
        </>
      }
    />
  );
};

export default ListIntegrations;
